// TS types
import { ICommonStore } from '@redux/Common/interfaces';
import { ITHSRStore } from '@redux/THSR/interfaces';
import { ITNormalStore } from '@redux/NormalT/interfaces';
import { scrollToRef, IOnSubmit, IValidateItem } from '@redux/common';
// Redux
import { store } from 'redux/store';
// api
import getEnvConfig from '@api/config';
import { fetchProductOrderValidity } from '@libs/apiCall';
// common function
import {
    formatProductDetailOrder,
    formatPassengerURL,
    openMemberLightBox,
    priceStrToNum,
    manageSessionStorage,
    parseApiResponse
} from '@libs/core';
import { storageKey } from '@libs/globalVariables';
// plugin
import dayjs from 'dayjs';
import { t } from 'i18next';
// redux actions
import {
    setTCalendarErrorMsg,
    setTSessionErrorMsg,
    setTTicketErrorMsg,
    setTScollTo,
    setTSubmitWatcher,
    setTSubmitErrMsg,
    setTIsSubmitSuccess,
} from '@redux/NormalT/actions';
import { setVerificationStatus } from '@redux/Common/actions';

const onSubmit: IOnSubmit = async (type) => {
    const dispatch = store.dispatch;
    const allStates = store.getState();
    const {
        ticketNum,
        NowListData,
        DetailData,
        pickCalendar,
        pickSession,
        submitWatcher,
        totalAmount,
    }: ITNormalStore = allStates.NormalT;
    const hasCalendar = NowListData?.ExpectedUseDay;
    const hasSession = DetailData?.Sessions;
    const submitDate = hasCalendar ? pickCalendar : dayjs().format('YYYY-MM-DD'); // 若非需選擇的日期的票券，則送當日日期

    const {
        ETID,
        isLogin,
        isErpOrder,
        ListData: { AdvanceTime },
    }: ICommonStore = allStates.Common;

    const {
        THSRInfo: { Thsr_OrgCurr_DiscountPrice, TicketsInfo, DiscountRule, ThsrDiscountPrice },
        THSRSearchOption: { seatClass, seatInfo },
        THSRDetailData: { Rounds },
        THSRDepartTrain,
        THSRReturnTrain,
    }: ITHSRStore = allStates.THSR;

    switch (type) {
        case 'checkData':
            dispatch(setTSubmitWatcher(!submitWatcher));

            //檢查月曆
            const checkCalendar = () => {
                if (pickCalendar) return true;
                dispatch(setTCalendarErrorMsg(t('THSR.onsubmit.selectDepartureDate')));
                return false;
            };

            //檢查場次
            const checkSession = () => {
                if (!hasSession) return true;
                if (pickSession) return true;
                dispatch(setTSessionErrorMsg(t('THSR.onsubmit.selectSessionTime')));
                return false;
            };

            //檢查票種
            const checkTicketNum = () => {
                if (!ticketNum) return false;
                const ticketVals = Object.values(ticketNum);
                if (typeof ticketVals === 'object' && ticketVals.length === 0) return false;
                const headCount = ticketVals.reduce((a: number, b: number) => a + b);
                if (headCount <= 0) {
                    dispatch(setTTicketErrorMsg(t('THSR.onsubmit.selectTicketTypeQuantity')));
                    return false;
                }
                if (headCount < TicketsInfo?.headCounts) {
                    dispatch(
                        setTTicketErrorMsg(
                            t('THSR.onsubmit.insufficientPurchases', { symbol: '！' })
                        )
                    );
                    return false;
                }
                return true;
            };

            // 檢查高鐵優惠價差為滿足錯誤
            const checkDiscount = () => {
                if (priceStrToNum(totalAmount) < DiscountRule) {
                    dispatch(
                        setTSubmitErrMsg(t('THSR.onsubmit.notEnoughForCoupon', { symbol: '！' }))
                    );
                    return false;
                }
                return true;
            };

            if (AdvanceTime.length > 0) return;

            let calendarStatus = checkCalendar();
            let sessionStatus = checkSession();
            let ticketNumStatus = checkTicketNum();
            let discountStatus = ticketNumStatus && checkDiscount();
            let apiData: IValidateItem | undefined;

            if (!calendarStatus) {
                dispatch(setTScollTo(scrollToRef.calender));
            } else if (!sessionStatus) {
                dispatch(setTScollTo(scrollToRef.session));
            } else if (!ticketNumStatus) {
                dispatch(setTScollTo(scrollToRef.ticket));
            } else if (!discountStatus) {
                dispatch(setTScollTo(scrollToRef.submitErr));
            }

            if (calendarStatus && sessionStatus && ticketNumStatus && discountStatus) {
                // 打驗證API確認當下時間產品上下限數量是否正確
                let order = formatProductDetailOrder(ticketNum);
                const checkValidate: IValidateItem = await parseApiResponse(() =>
                    fetchProductOrderValidity(ETID, order, submitDate)
                );

                apiData = checkValidate;
                dispatch(setVerificationStatus(checkValidate));
                if (checkValidate.IsSuccess) {
                    return {
                        selectChecked: true,
                        apiChecked: checkValidate,
                    };
                }
            }
            return { selectChecked: false, apiChecked: apiData ? apiData : {} };
        case 'redirect':
            const submitURL = getEnvConfig().normal.submitURL;

            let paramURL = formatPassengerURL({
                ETID,
                ticketNum,
                pickCalendar: submitDate,
                hasCalendar,
                pickSession: pickSession ? pickSession : '',
            });

            const roundsArr = Rounds?.map((item, index) => {
                const isReturn = index === 1; // 來回則會有兩筆，第二筆使用回程資料
                return {
                    departDate: item.DepartDate,
                    departStation: item.DepartStation?.value,
                    departStationTxt: item.DepartStation?.text,
                    departTime: isReturn
                        ? THSRReturnTrain?.DepartTime
                        : THSRDepartTrain?.DepartTime,
                    arriveStation: item.ArriveStation?.value,
                    arriveStationTxt: item.ArriveStation?.text,
                    arriveTime: isReturn
                        ? THSRReturnTrain?.ArriveTime
                        : THSRDepartTrain?.ArriveTime,
                    trainNumber: isReturn
                        ? THSRReturnTrain?.TrainNumber
                        : THSRDepartTrain?.TrainNumber,
                    duration: isReturn ? THSRReturnTrain?.Duration : THSRDepartTrain?.Duration,
                };
            });

            const THSRSaveStorage = {
                productID: ETID,
                round: roundsArr,
                trainClass: seatClass,
                ticketsQty: seatInfo,
                totalHeadCount: TicketsInfo?.headCounts,
                THSRtotalPrice: TicketsInfo?.totalPrice,
                THSRDiscountPrice: ThsrDiscountPrice,
                Thsr_OrgCurr_DiscountPrice: Thsr_OrgCurr_DiscountPrice,
                THSRUnitPrice: {
                    Erat: TicketsInfo?.Erat,
                    ThsrDiscountPrice: TicketsInfo?.ThsrDiscountPrice,
                    Thsr_OrgCurr_DiscountPrice: TicketsInfo?.Thsr_OrgCurr_DiscountPrice,
                    adultPrice: TicketsInfo?.adultPrice,
                    adult_OrgCurr_Price: TicketsInfo?.adult_OrgCurr_Price,
                    concessionPrice: TicketsInfo?.concessionPrice,
                    concession_OrgCurr_Price: TicketsInfo?.concession_OrgCurr_Price,
                    total_OrgCurr_Price: TicketsInfo?.total_OrgCurr_Price,
                },
                PassengerUseDate: pickCalendar,
            };

            /**
             *   最終導頁網址(確認是否為IT本地開發，IT本地網域不同)&光箱關閉事件
             *   IT本地&F2E本地在判斷環境上不相同(意謂IT本地在process.env.SERVER !== dev)
             *   https://udeveloper.liontravel.com:44369/passenger${paramURL}
             **/
            const finalUrl =
                window.location.hostname === 'udeveloper.liontravel.com'
                    ? `${window.location.origin}/order/passenger${paramURL}`
                    : `${submitURL}${paramURL}`;
            const closeMemberFuc = () => {
                console.log('Close memberLightBox');
                dispatch(setTIsSubmitSuccess(false));
            };

            //儲存高鐵資料&IT認證參數
            manageSessionStorage('save', storageKey, THSRSaveStorage);
            manageSessionStorage('save', 'OrderProcess', 'FromDetail');

            // 確認登入狀態，未登入的話呼叫會員光箱
            if (isLogin) {
                window.location.href = finalUrl;
            } else {
                if (process.env.SERVER === 'dev' || isErpOrder) {
                    // 本機(沒有辦法呼叫會員光箱)或有電話單直接跳轉
                    window.location.href = finalUrl;
                } else {
                    // IT本地、測試&正式機(沒電話單情況)呼叫會員光箱跳轉
                    openMemberLightBox(finalUrl, closeMemberFuc);
                }
            }
            break;
    }
};
export default onSubmit;
